import { diffWords, diffJson } from "diff";

// Helper function to safely parse and format JSON
function safeParseAndFormat(input?: string | null): string {
  if (!input || input.trim() === "" || input.trim() === "null") {
    return "{}";
  }
  
  try {
    const parsed = JSON.parse(input);
    return JSON.stringify(parsed, null, 2);
  } catch {
    return input.trim();
  }
}

// Enhanced diff function for JSON objects
export function computeDiffHtml(oldStr?: string | null, newStr?: string | null) {
  const safeOld = safeParseAndFormat(oldStr);
  const safeNew = safeParseAndFormat(newStr);

  // Handle creation case (old is empty/null)
  if (!oldStr || oldStr.trim() === "" || oldStr.trim() === "null" || safeOld === "{}") {
    return (
      <div>
        <div style={{ color: '#666', fontStyle: 'italic', marginBottom: '8px' }}>
          Initial creation - no previous data
        </div>
        <span style={{ backgroundColor: "rgba(0,255,0,0.2)" }}>
          {safeNew}
        </span>
      </div>
    );
  }

  // Handle deletion case (new is empty/null)
  if (!newStr || newStr.trim() === "" || newStr.trim() === "null" || safeNew === "{}") {
    return (
      <div>
        <div style={{ color: '#666', fontStyle: 'italic', marginBottom: '8px' }}>
          Data removed
        </div>
        <span style={{ backgroundColor: "rgba(255,0,0,0.2)", textDecoration: "line-through" }}>
          {safeOld}
        </span>
      </div>
    );
  }

  try {
    // Try JSON diff first for better structured comparison
    const jsonDiff = diffJson(safeOld, safeNew);
    
    return jsonDiff.map((part, idx) => {
      const style = {
        backgroundColor: part.added ? "rgba(0,255,0,0.2)" :
                         part.removed ? "rgba(255,0,0,0.2)" : "transparent",
        textDecoration: part.removed ? "line-through" : "none",
        display: "block",
        whiteSpace: "pre-wrap" as const,
        margin: "2px 0"
      };
      
      return (
        <span key={idx} style={style}>
          {part.value}
        </span>
      );
    });
  } catch (err) {
    // Fallback to word diff if JSON diff fails
    console.warn("JSON diff failed, falling back to word diff:", err);
    
    return diffWords(safeOld, safeNew).map((part, idx) => {
      const style = {
        backgroundColor: part.added ? "rgba(0,255,0,0.2)" :
                         part.removed ? "rgba(255,0,0,0.2)" : "transparent",
        textDecoration: part.removed ? "line-through" : "none"
      };
      return (
        <span key={idx} style={style}>
          {part.value}
        </span>
      );
    });
  }
}

// Helper function to get a summary of changes
export function getChangeSummary(oldStr?: string | null, newStr?: string | null): string {
  const safeOld = safeParseAndFormat(oldStr);
  const safeNew = safeParseAndFormat(newStr);

  if (!oldStr || oldStr.trim() === "" || oldStr.trim() === "null" || safeOld === "{}") {
    return "Initial creation";
  }

  if (!newStr || newStr.trim() === "" || newStr.trim() === "null" || safeNew === "{}") {
    return "Data removed";
  }

  try {
    const oldObj = JSON.parse(safeOld);
    const newObj = JSON.parse(safeNew);
    
    const oldKeys = Object.keys(oldObj);
    const newKeys = Object.keys(newObj);
    
    const addedKeys = newKeys.filter(key => !oldKeys.includes(key));
    const removedKeys = oldKeys.filter(key => !newKeys.includes(key));
    const modifiedKeys = oldKeys.filter(key => 
      newKeys.includes(key) && JSON.stringify(oldObj[key]) !== JSON.stringify(newObj[key])
    );

    const changes = [];
    if (addedKeys.length > 0) changes.push(`Added: ${addedKeys.join(', ')}`);
    if (removedKeys.length > 0) changes.push(`Removed: ${removedKeys.join(', ')}`);
    if (modifiedKeys.length > 0) changes.push(`Modified: ${modifiedKeys.join(', ')}`);

    return changes.length > 0 ? changes.join(' | ') : "No changes detected";
  } catch {
    return "Data updated";
  }
}